"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  DEFAULT_THEME_ID,
  resolveThemeRoles,
  STORAGE_KEY,
  THEMES,
  type Theme,
} from "./theme";

type EditorialThemeContextValue = {
  theme: Theme;
  setTheme: (t: Theme) => void;
  themes: Theme[];
};

const EditorialThemeContext = createContext<EditorialThemeContextValue | null>(
  null,
);

/** Apply source colours and semantic roles to <html> so themed pages inherit
 *  them and the values survive client navigation. */
function applyThemeVars(t: Theme) {
  if (typeof document === "undefined") return;
  const d = document.documentElement;
  const roles = resolveThemeRoles(t);
  d.style.setProperty("--theme-dark", t.dark);
  d.style.setProperty("--theme-accent", t.accent);
  d.style.setProperty("--theme-ground", t.ground);
  d.style.setProperty("--theme-ink", t.ink);
  d.style.setProperty("--theme-surface-dark", roles.surfaceDark);
  d.style.setProperty("--theme-accent-text", roles.accentText);
}

export function EditorialThemeProvider({
  children,
  defaultThemeId = DEFAULT_THEME_ID,
}: {
  children: ReactNode;
  defaultThemeId?: string;
}) {
  const [theme, setThemeState] = useState<Theme>(
    () => THEMES.find((t) => t.id === defaultThemeId) ?? THEMES[0]!,
  );

  // Load the stored palette on mount. The root layout supplies the default
  // variables, then this synchronises the stored palette and active swatch.
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      const match = THEMES.find((t) => t.id === stored);
      if (match) setThemeState(match);
    } catch {
      // localStorage may be unavailable.
    }
  }, []);

  // Keep <html> variables in sync whenever the active palette changes.
  useEffect(() => {
    applyThemeVars(theme);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next.id);
    } catch {
      // localStorage may be unavailable.
    }
  }, []);
  const value = useMemo(
    () => ({ theme, setTheme, themes: THEMES }),
    [setTheme, theme],
  );

  return (
    <EditorialThemeContext.Provider value={value}>
      {children}
    </EditorialThemeContext.Provider>
  );
}

export function useEditorialTheme(): EditorialThemeContextValue {
  const ctx = useContext(EditorialThemeContext);
  if (!ctx) {
    throw new Error(
      "useEditorialTheme must be used within an EditorialThemeProvider",
    );
  }
  return ctx;
}
